'use client';
import { APP_ROUTES } from '@constants/appRoutes';
import { StorageKeys } from '@constants/storageKeys';
import { navigate } from '@utils/navigate';
import { useSearchParams, useParams, useRouter } from 'next/navigation';

/**
 * Converts an object to a query string, optionally filtering by valid parameters.
 * @param params - The object to convert to query parameters.
 * @param VALID_SEARCH_PARAMS - Optional array of valid parameter keys.
 * @returns A URL-encoded query string.
 */
export const objectToQueryParams = (params: Record<string, any>, VALID_SEARCH_PARAMS?: string[]) => {
  const filteredParams = Object.entries(params).filter(
    ([key, value]) => {
      if (VALID_SEARCH_PARAMS && !VALID_SEARCH_PARAMS.includes(key)) return false;
      return (value !== '' && value?.length !== 0);
    }
  );
  return new URLSearchParams(filteredParams).toString();
};

/**
 * Parses a URLSearchParams object into a plain object, optionally filtering by valid parameters.
 * @param searchParam - The URLSearchParams object to parse.
 * @param VALID_SEARCH_PARAMS - Optional array of valid parameter keys.
 * @returns An object containing the parsed query parameters.
 */
export function parseQueryObjectFromQueryParam(searchParam: URLSearchParams, VALID_SEARCH_PARAMS?: string[]) {
  const entries = Array.from(searchParam.entries());
  const validEntries = VALID_SEARCH_PARAMS
    ? entries.filter(([key]) => VALID_SEARCH_PARAMS.includes(key))
    : entries;
  return Object.fromEntries(validEntries) ?? {};
}

/**
 * Custom hook for handling routing with language support and query parameters.
 * @returns An object with utility functions for navigation and path manipulation.
 */
export const useAppRouter = () => {
  const { lang } = useParams();
  const searchParams = useSearchParams();
  const router = useRouter();

  /**
   * Prepends the base path (including language) to a given path.
   * @param path - The relative path to append.
   * @param queryStr - Optional query string to append.
   * @returns The full path with the base and optional query string.
   */
  const getPathWithBase = (path: string, queryStr?: string) => {
    return `/${lang}${path.startsWith('/') ? path : `/${path}`}${queryStr ? `?${queryStr}` : ''}`;
  };

  /**
   * Generates a full path with query parameters.
   * @param path - The relative path.
   * @param query - An object containing query parameters.
   * @returns The full path with query parameters.
   */
  const getPathWithQuery = (path: string, query: Record<string, string>) => {
    const queryStr = objectToQueryParams(query);
    return `${getPathWithBase(path)}?${queryStr}`;
  };

  /**
   * Navigates to a new path, merging new query parameters with existing ones.
   * @param path - The relative path to navigate to.
   * @param query - An object containing new query parameters.
   */
  const navigateTo = (path: string, query: Record<string, string>) => {
    const queryObj = {
      ...parseQueryObjectFromQueryParam(searchParams),
      ...query
    };
    return navigate(router, getPathWithQuery(path, queryObj));
  }

  /**
   * Navigates back to the previous path, optionally with a fallback.
   * @param fallback - The path to navigate back to. Defaults to the home page.
   */
  const navigateToPrevious = (fallback = APP_ROUTES.HOME) => {
    const previousPath = sessionStorage.getItem(StorageKeys.PreviousPath);

    const isInternalRoute =
      previousPath &&
      previousPath.startsWith('/');

    if (isInternalRoute) {
      router.back();
    } else {
      navigate(router, getPathWithBase(fallback));
    }
  }

  return { getPathWithBase, getPathWithQuery, navigateTo, parseQueryObjectFromQueryParam, navigateToPrevious };
};
